/**
 * Composable for managing VAST ad lifecycle (pre-roll, mid-roll, post-roll).
 *
 * Handles ad loading, scheduling, real video playback, skip logic,
 * and IAB-compliant event tracking via VASTTracker.
 */

import { ref, shallowRef, shallowReadonly, computed, watch, onUnmounted, type Ref, type ComputedRef } from 'vue'
import { VASTService } from '../services/vast/VASTService'
import { VASTTracker } from '../services/vast/VASTTracker'
import type { VASTAdConfig, ParsedAd, AdBreak } from '../services/vast/types'
import { VASTError, VASTErrorCode } from '../services/vast/types'

export interface AdsConfig {
  preRoll?: VASTAdConfig[]
  midRolls?: Array<{ time: number; ads: VASTAdConfig[] }>
  postRoll?: VASTAdConfig[]
}

export interface UseAdsManagerOptions {
  onPauseContent: () => void
  onResumeContent: () => void
  currentTime: Ref<number>
  isPlaying: Ref<boolean> | ComputedRef<boolean>
  isEnded?: Ref<boolean> | ComputedRef<boolean>
  volume?: Ref<number>
  isMuted?: Ref<boolean>
  isFullscreen?: Ref<boolean> | ComputedRef<boolean>
  playerWidth?: Ref<number>
  playerHeight?: Ref<number>
  adsConfig?: Ref<AdsConfig | undefined>
  onAdError?: (error: VASTError) => void
}

export function useAdsManager(options: UseAdsManagerOptions) {
  const {
    onPauseContent,
    onResumeContent,
    currentTime,
    isPlaying,
    isEnded,
    volume,
    isMuted,
    isFullscreen,
    playerWidth,
    playerHeight,
    adsConfig,
    onAdError,
  } = options

  // Built on the first ad break rather than with the player: most embeds carry no ads, and the
  // VAST client it wraps has no reason to exist in them.
  let vastService: VASTService | null = null
  const vast = (): VASTService => (vastService ??= new VASTService())

  // Ad video element — assigned by AdsPlayer.vue through setAdVideo, and read-only to everyone else
  const adVideoRef = shallowRef<HTMLVideoElement | null>(null)

  /**
   * The ad layer's element, or `null` when the layer goes. The element it replaces is let go of
   * here — its listeners, its playback, its source — because the layer unmounts before the
   * player's own teardown runs `clearAds`, which would then find no element to clean.
   */
  function setAdVideo(element: HTMLVideoElement | null): void {
    const previous = adVideoRef.value
    if (previous === element) return
    if (previous) {
      unbindAdVideoEvents()
      previous.pause()
      previous.removeAttribute('src')
      previous.load()
    }
    adVideoRef.value = element
  }

  // Ad state
  const isAdPlaying = ref(false)
  const isAdSkippable = ref(false)
  const adCurrentTime = ref(0)
  const adDuration = ref(0)
  const adTimeRemaining = ref(0)
  const currentAd = ref<ParsedAd | null>(null)
  const adBreaks = ref<AdBreak[]>([])
  const adQueue = ref<ParsedAd[]>([])
  const currentAdIndex = ref(0)
  const totalAdsInBreak = ref(0)
  const isLoadingAd = ref(false)

  // Tracker for the currently playing ad
  let currentTracker: VASTTracker | null = null

  const hasAds = computed(() => {
    const config = adsConfig?.value
    return !!(config?.preRoll?.length || config?.midRolls?.length || config?.postRoll?.length)
  })

  const hasAdsQueued = computed(() => adQueue.value.length > 0)

  const adClickThrough = computed(() => currentAd.value?.clickThrough)

  // =========================================================================
  // Ad video event handlers
  // =========================================================================

  function onAdTimeUpdate(): void {
    const video = adVideoRef.value
    if (!video || !currentAd.value) return

    adCurrentTime.value = video.currentTime
    adDuration.value = video.duration || currentAd.value.duration
    adTimeRemaining.value = Math.max(0, adDuration.value - video.currentTime)

    // Check skip offset
    const skipOffset = currentAd.value.skipOffset
    if (skipOffset !== undefined && skipOffset >= 0 && video.currentTime >= skipOffset) {
      isAdSkippable.value = true
    }

    // Update tracker
    if (currentTracker) {
      currentTracker.updatePlayhead(currentTime.value, video.currentTime)
      currentTracker.checkQuartiles(video.currentTime, adDuration.value)
    }
  }

  function onAdEnded(): void {
    if (currentTracker) {
      currentTracker.trackComplete()
    }
    onAdComplete()
  }

  function handleAdVideoError(): void {
    console.warn('[useAdsManager] Ad video playback error')
    if (currentTracker) {
      currentTracker.trackError(VASTErrorCode.MEDIA_FILE_DISPLAY_ERROR)
    }
    onAdComplete()
  }

  function onAdLoadedMetadata(): void {
    const video = adVideoRef.value
    if (video && video.duration) {
      adDuration.value = video.duration
      adTimeRemaining.value = video.duration
    }
  }

  function onAdPlay(): void {
    if (currentTracker) {
      currentTracker.trackImpression()
    }
  }

  function bindAdVideoEvents(): void {
    const video = adVideoRef.value
    if (!video) return
    video.addEventListener('timeupdate', onAdTimeUpdate)
    video.addEventListener('ended', onAdEnded)
    video.addEventListener('error', handleAdVideoError)
    video.addEventListener('loadedmetadata', onAdLoadedMetadata)
    video.addEventListener('play', onAdPlay)
  }

  function unbindAdVideoEvents(): void {
    const video = adVideoRef.value
    if (!video) return
    video.removeEventListener('timeupdate', onAdTimeUpdate)
    video.removeEventListener('ended', onAdEnded)
    video.removeEventListener('error', handleAdVideoError)
    video.removeEventListener('loadedmetadata', onAdLoadedMetadata)
    video.removeEventListener('play', onAdPlay)
  }

  // =========================================================================
  // Ad scheduling
  // =========================================================================

  function initializeAdBreaks(config: AdsConfig): void {
    const breaks: AdBreak[] = []

    if (config.preRoll?.length) {
      breaks.push({ id: 'pre-roll', time: 0, ads: config.preRoll, played: false })
    }

    if (config.midRolls) {
      config.midRolls.forEach((midRoll, index) => {
        breaks.push({ id: `mid-roll-${index}`, time: midRoll.time, ads: midRoll.ads, played: false })
      })
    }

    if (config.postRoll?.length) {
      breaks.push({ id: 'post-roll', time: -1, ads: config.postRoll, played: false })
    }

    adBreaks.value = breaks
  }

  async function loadAds(adConfigs: VASTAdConfig[]): Promise<void> {
    const parsedAds: ParsedAd[] = []

    for (const adConfig of adConfigs) {
      try {
        const parsed = await vast().loadVAST(adConfig)
        if (parsed.length > 0) {
          parsedAds.push(...parsed)
        }
      } catch {
        // Ad loading failures (no fill, network error, invalid XML, etc.)
        // are never critical — silently continue to the next ad or content.
      }
    }

    adQueue.value = parsedAds
    totalAdsInBreak.value = parsedAds.length
    currentAdIndex.value = 0
  }

  // =========================================================================
  // Ad playback
  // =========================================================================

  async function playNextAd(): Promise<void> {
    if (adQueue.value.length === 0) {
      isAdPlaying.value = false
      return
    }

    const ad = adQueue.value.shift()!
    currentAd.value = ad
    currentAdIndex.value = totalAdsInBreak.value - adQueue.value.length - 1
    isAdPlaying.value = true
    isAdSkippable.value = false
    adCurrentTime.value = 0
    adDuration.value = ad.duration
    adTimeRemaining.value = ad.duration

    // Create tracker for this ad
    const pw = playerWidth?.value || 0
    const ph = playerHeight?.value || 0
    currentTracker = new VASTTracker(ad, pw, ph)

    // Select best media file and start playback
    const video = adVideoRef.value
    if (!video) {
      console.error('[useAdsManager] No ad video element available')
      onAdComplete()
      return
    }

    bindAdVideoEvents()

    const mediaFile = vast().selectBestMediaFile(ad.mediaFiles, pw || 640, ph || 360)
    if (!mediaFile) {
      console.error('[useAdsManager] No suitable media file found')
      if (currentTracker) {
        currentTracker.trackError(VASTErrorCode.MEDIA_FILE_NO_SUPPORTED_TYPE)
      }
      onAdComplete()
      return
    }

    video.src = mediaFile.url

    // Sync volume with content player
    if (volume?.value !== undefined) {
      video.volume = volume.value
    }
    if (isMuted?.value !== undefined) {
      video.muted = isMuted.value
    }

    try {
      await video.play()
    } catch {
      // Autoplay blocked — retry muted (browsers allow muted autoplay)
      try {
        video.muted = true
        await video.play()
      } catch {
        console.error('[useAdsManager] Ad autoplay failed even muted, skipping')
        if (currentTracker) {
          currentTracker.trackError(VASTErrorCode.MEDIA_FILE_DISPLAY_ERROR)
        }
        onAdComplete()
      }
    }
  }

  function onAdComplete(): void {
    unbindAdVideoEvents()

    const video = adVideoRef.value
    if (video) {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }

    currentAd.value = null
    currentTracker = null
    isAdSkippable.value = false
    adCurrentTime.value = 0
    adDuration.value = 0
    adTimeRemaining.value = 0

    if (hasAdsQueued.value) {
      currentAdIndex.value++
      playNextAd()
    } else {
      isAdPlaying.value = false
    }
  }

  // =========================================================================
  // Public API
  // =========================================================================

  async function initializeAds(): Promise<void> {
    const config = adsConfig?.value
    if (!config) return

    initializeAdBreaks(config)

    // Load and play pre-roll if available
    const preRollBreak = adBreaks.value.find(ab => ab.id === 'pre-roll')
    if (preRollBreak && !preRollBreak.played) {
      preRollBreak.played = true
      isLoadingAd.value = true
      try {
        await loadAds(preRollBreak.ads)
        if (hasAdsQueued.value) {
          onPauseContent()
          await playNextAd()
        }
      } catch {
        // Silently handled — content plays without ads
      } finally {
        isLoadingAd.value = false
      }
    }
  }

  function skipAd(): void {
    if (!isAdSkippable.value) return
    if (currentTracker) {
      currentTracker.trackSkip()
    }
    onAdComplete()
  }

  function pauseAd(): void {
    adVideoRef.value?.pause()
    if (currentTracker) {
      currentTracker.trackPause()
    }
  }

  function resumeAd(): void {
    if (!currentAd.value || !isAdPlaying.value) return
    adVideoRef.value?.play()
    if (currentTracker) {
      currentTracker.trackResume()
    }
  }

  /** Opens the advertiser's page, counts the click and pauses the ad behind it. */
  function openAdvertiser(): void {
    if (!currentAd.value?.clickThrough) return
    window.open(currentAd.value.clickThrough, '_blank', 'noopener')
    if (currentTracker) {
      currentTracker.trackClick()
    }
    pauseAd()
  }

  /**
   * A click on the ad itself. The ad a click-through paused has no play button,
   * so the next click resumes it rather than opening the advertiser again.
   */
  function handleAdClick(): void {
    if (adVideoRef.value?.paused) {
      resumeAd()
      return
    }
    openAdvertiser()
  }

  function setAdMuted(muted: boolean): void {
    const video = adVideoRef.value
    if (!video) return
    video.muted = muted
    if (currentTracker) {
      if (muted) currentTracker.trackMute()
      else currentTracker.trackUnmute()
    }
  }

  function setAdVolume(vol: number): void {
    const video = adVideoRef.value
    if (!video) return
    video.volume = Math.max(0, Math.min(1, vol))
  }

  function clearAds(): void {
    unbindAdVideoEvents()
    const video = adVideoRef.value
    if (video) {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
    adBreaks.value = []
    adQueue.value = []
    currentAd.value = null
    currentTracker = null
    isAdPlaying.value = false
    isAdSkippable.value = false
    adCurrentTime.value = 0
    adDuration.value = 0
    adTimeRemaining.value = 0
    totalAdsInBreak.value = 0
    currentAdIndex.value = 0
    isLoadingAd.value = false
  }

  // =========================================================================
  // Watchers
  // =========================================================================

  // Resume content when ad playback ends
  watch(isAdPlaying, (playing, wasPlaying) => {
    if (wasPlaying && !playing) {
      onResumeContent()
    }
  })

  // Check for mid-roll triggers based on content playback time
  watch(currentTime, (time) => {
    if (!hasAds.value || !isPlaying.value || isAdPlaying.value || isLoadingAd.value) return

    const adBreak = adBreaks.value.find(
      ab => ab.time > 0 && !ab.played && time >= ab.time,
    )

    if (adBreak) {
      adBreak.played = true
      isLoadingAd.value = true
      loadAds(adBreak.ads)
        .then(() => {
          if (hasAdsQueued.value) {
            onPauseContent()
            playNextAd()
          }
        })
        .catch(() => {})
        .finally(() => {
          isLoadingAd.value = false
        })
    }
  })

  // Trigger post-roll when content video ends
  if (isEnded) {
    watch(isEnded, (ended) => {
      if (!ended) return
      const postRoll = adBreaks.value.find(ab => ab.time === -1 && !ab.played)
      if (!postRoll) return

      postRoll.played = true
      isLoadingAd.value = true
      loadAds(postRoll.ads)
        .then(() => {
          if (hasAdsQueued.value) {
            playNextAd()
          }
        })
        .catch(() => {})
        .finally(() => {
          isLoadingAd.value = false
        })
    })
  }

  // Sync volume changes from content player to ad video
  if (volume) {
    watch(volume, (vol) => {
      if (isAdPlaying.value && adVideoRef.value) {
        adVideoRef.value.volume = vol
      }
    })
  }

  if (isMuted) {
    watch(isMuted, (muted) => {
      if (isAdPlaying.value && adVideoRef.value) {
        adVideoRef.value.muted = muted
      }
    })
  }

  // Track fullscreen changes
  if (isFullscreen) {
    watch(isFullscreen, (fs) => {
      if (currentTracker) {
        if (fs) currentTracker.trackFullscreen()
        else currentTracker.trackExitFullscreen()
      }
    })
  }

  // Watch for config changes
  if (adsConfig) {
    watch(adsConfig, (newConfig) => {
      clearAds()
      if (newConfig) {
        initializeAdBreaks(newConfig)
      }
    })
  }

  onUnmounted(() => {
    clearAds()
  })

  return {
    // State
    adVideoRef: shallowReadonly(adVideoRef),
    isAdPlaying,
    isAdSkippable,
    adCurrentTime,
    adDuration,
    adTimeRemaining,
    currentAd,
    hasAds,
    hasAdsQueued,
    adBreaks,
    currentAdIndex,
    totalAdsInBreak,
    adClickThrough,
    isLoadingAd,

    // Actions
    setAdVideo,
    initializeAds,
    skipAd,
    pauseAd,
    resumeAd,
    handleAdClick,
    openAdvertiser,
    setAdMuted,
    setAdVolume,
    clearAds,
  }
}

export type UseAdsManagerReturn = ReturnType<typeof useAdsManager>
